import { normalizeSupplierOrder, type SupplierOrderDto } from '../../../dto/supplierOrder';
import {
  stripSupplierOrderPurchaseFields,
  type SupplierOrderItemPayload,
  type SupplierOrderPayload,
} from '../api/supplierOrdersApi';
import { buildSupplierOrderMoreDataFields, type SupplierOrderMoreDataValues } from '../supplierOrderDetailEditUtils';
import { retargetLineCurrencies } from '../supplierOrderCurrency';

/**
 * A new ZD before its first save (karta ZD według makiety, 2026-10-05):
 * `/app/supplier-orders/new` is the ZD card in stage „Szkic” without a record.
 * Lines and „Więcej danych” live in the page until the supplier is chosen —
 * the backend saves a ZD only with a supplier
 * (supplierOrderService.supplierRequiredError) — and the choice creates the
 * ZD through the ordinary POST /supplier-orders with status „Szkic”.
 */
export interface UnsavedSupplierOrderDraft {
  currency: string;
  items: SupplierOrderItemPayload[];
  more: SupplierOrderMoreDataValues;
}

export function buildNewSupplierOrderPayload({
  draft,
  supplierId,
  currency,
  canEditPurchasePrices,
}: {
  draft: UnsavedSupplierOrderDraft;
  supplierId: string;
  /** Currency picked in „Wybierz dostawcę” (price editors only); null keeps the draft's one. */
  currency?: string | null;
  canEditPurchasePrices: boolean;
}): SupplierOrderPayload {
  const nextCurrency = currency || draft.currency || 'PLN';
  const items = retargetLineCurrencies(draft.items, draft.currency || 'PLN', nextCurrency);
  const payload: SupplierOrderPayload = {
    supplierId,
    status: 'DRAFT',
    currency: nextCurrency,
    items,
    ...buildSupplierOrderMoreDataFields(draft.more),
  };
  return canEditPurchasePrices ? payload : stripSupplierOrderPurchaseFields(payload);
}

/**
 * The unsaved ZD as a read model for the shared card sections (items table,
 * „Pozycje i ceny”). It has no id, number, supplier or status history.
 */
export function buildUnsavedSupplierOrderView(draft: UnsavedSupplierOrderDraft): SupplierOrderDto {
  return normalizeSupplierOrder({
    id: '',
    orderNumber: '',
    supplierId: '',
    status: 'DRAFT',
    currency: draft.currency,
    items: draft.items,
  }, 'supplierOrders.newDraft');
}
